import React, { useState, useEffect } from 'react';
import { getSession, getScenarios, submitSoapNote, getSubmissionCount } from '../services/api';

const styles = {
  card: {
    background: '#fff',
    borderRadius: '12px',
    padding: '40px',
    width: '100%',
    boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
  },
  heading: {
    fontSize: '24px',
    fontWeight: '700',
    color: '#1a202c',
    marginBottom: '24px',
  },
  infoBar: {
    display: 'flex',
    gap: '24px',
    padding: '16px',
    background: '#f7fafc',
    borderRadius: '8px',
    marginBottom: '24px',
  },
  infoItem: {
    fontSize: '14px',
  },
  infoLabel: {
    color: '#718096',
    fontSize: '12px',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  infoValue: {
    color: '#1a202c',
    fontWeight: '600',
    fontSize: '16px',
  },
  label: {
    display: 'block',
    fontSize: '14px',
    fontWeight: '600',
    color: '#4a5568',
    marginBottom: '6px',
  },
  select: {
    width: '100%',
    padding: '12px 16px',
    border: '2px solid #e2e8f0',
    borderRadius: '8px',
    fontSize: '16px',
    outline: 'none',
    marginBottom: '20px',
    background: '#fff',
  },
  dropZone: {
    border: '2px dashed #cbd5e0',
    borderRadius: '8px',
    padding: '40px 20px',
    textAlign: 'center',
    cursor: 'pointer',
    marginBottom: '20px',
    transition: 'all 0.2s',
    background: '#f7fafc',
  },
  dropZoneActive: {
    border: '2px dashed #9C3D97',
    background: '#f8f0f8',
  },
  dropZoneHasFile: {
    border: '2px solid #48bb78',
    background: '#f0fff4',
  },
  dropIcon: {
    fontSize: '36px',
    marginBottom: '8px',
    color: '#a0aec0',
  },
  dropText: {
    fontSize: '16px',
    color: '#4a5568',
    marginBottom: '4px',
  },
  dropSubtext: {
    fontSize: '13px',
    color: '#a0aec0',
  },
  fileName: {
    fontSize: '16px',
    color: '#276749',
    fontWeight: '600',
    marginBottom: '4px',
  },
  fileSize: {
    fontSize: '13px',
    color: '#68d391',
  },
  removeFile: {
    fontSize: '13px',
    color: '#e53e3e',
    cursor: 'pointer',
    marginTop: '8px',
    background: 'none',
    border: 'none',
    textDecoration: 'underline',
  },
  button: {
    width: '100%',
    padding: '14px',
    background: '#9C3D97',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  buttonDisabled: {
    opacity: 0.6,
    cursor: 'not-allowed',
  },
  error: {
    background: '#fed7d7',
    color: '#c53030',
    padding: '12px 16px',
    borderRadius: '8px',
    marginBottom: '20px',
    fontSize: '14px',
  },
  warning: {
    background: '#fffaf0',
    color: '#975a16',
    padding: '12px 16px',
    borderRadius: '8px',
    marginBottom: '20px',
    fontSize: '14px',
    border: '1px solid #fbd38d',
  },
};

function formatFileSize(bytes) {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / 1048576).toFixed(1) + ' MB';
}

export default function SubmissionPage({ onSuccess }) {
  const session = getSession();
  const [scenarios, setScenarios] = useState([]);
  const [selectedScenario, setSelectedScenario] = useState('');
  const [pdfFile, setPdfFile] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [subsUsed, setSubsUsed] = useState(0);
  const [subsMax, setSubsMax] = useState(75);
  const [gradedCount, setGradedCount] = useState(0);
  const [gradingCount, setGradingCount] = useState(0);
  const [gradingComplete, setGradingComplete] = useState(false);
  const [submissionHistory, setSubmissionHistory] = useState([]);

  useEffect(() => {
    async function loadData() {
      try {
        const [scenData, countData] = await Promise.all([getScenarios(), getSubmissionCount()]);
        setScenarios(scenData.scenarios || []);
        setSubsUsed(countData.submissions_used || 0);
        setSubsMax(countData.submissions_max || 75);
        setGradedCount(countData.graded_count || 0);
        setGradingCount(countData.grading_count || 0);
        setGradingComplete(countData.grading_complete || false);
        setSubmissionHistory(countData.submissions || []);
      } catch {
        setError('Failed to load data.');
      }
    }
    loadData();
  }, []);

  function handleFile(file) {
    setError('');
    if (!file) return;
    if (file.type !== 'application/pdf') {
      setError('Please upload a PDF file.');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setError('File size must be under 10 MB.');
      return;
    }
    setPdfFile(file);
  }

  function handleDrop(e) {
    e.preventDefault();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  }

  function handleDragOver(e) {
    e.preventDefault();
    setDragActive(true);
  }

  function handleDragLeave(e) {
    e.preventDefault();
    setDragActive(false);
  }

  function getFilenameMismatch() {
    if (!selectedScenario || !pdfFile) return null;
    const scenario = scenarios.find((s) => s.id === selectedScenario);
    if (!scenario) return null;

    const fileBase = pdfFile.name.replace(/\.pdf$/i, '');
    const normalize = (str) => str.toLowerCase().replace(/[^a-z0-9]/g, '');
    const fileNorm = normalize(fileBase);

    // Parenthetical suffixes like "(Patient 1 Day 1)" are slot labels the
    // admin added when reusing a case across batches. They are not part of
    // the distinguishing case identifier, so strip them before matching.
    const coreName = scenario.name.replace(/\([^)]*\)/g, ' ');

    // Day/Patient identifier gate. When a case name contains "Day N" and/or
    // "Patient M", those numbers are identity, not just hints — uploading a
    // Day 4 file for a Day 1 case is a mistake we MUST flag. The regex is
    // lenient on separators (space, _, -, ., ,, or none) so messy human
    // filenames like "Day_1", "Day-1", "day1", "DAY 01" all parse, but the
    // captured number is then compared numerically (so 1==01, 1!=10).
    // The (?<![a-z]) negative-lookbehind keeps "thursday" / "subpatient" from
    // false-triggering. It is non-consuming (unlike the old [^a-z] anchor,
    // which ate the boundary char and dropped the second number in jammed
    // tokens like "Day1Day2" -> [1] instead of [1,2]).
    const buildIdRegex = (kw) =>
      new RegExp(`(?<![a-z])${kw}[\\s\\-_.,]*?(\\d+)`, 'gi');
    const extractNumbers = (text, kw) => {
      const out = [];
      const re = buildIdRegex(kw);
      let m;
      while ((m = re.exec(text)) !== null) {
        out.push(parseInt(m[1], 10));
      }
      return out;
    };

    const caseDays = extractNumbers(coreName, 'day');
    const casePatients = extractNumbers(coreName, 'patient');

    if (caseDays.length > 0) {
      const fileDays = extractNumbers(fileBase, 'day');
      const expected = caseDays[0]; // case names should only have one
      if (!fileDays.includes(expected)) {
        const where = fileDays.length === 0
          ? 'has no day number'
          : `references Day ${fileDays.join(', Day ')}`;
        return `File name "${pdfFile.name}" does not match the selected case "${scenario.name}". Expected Day ${expected}, but the filename ${where}.`;
      }
    }

    if (casePatients.length > 0) {
      const filePatients = extractNumbers(fileBase, 'patient');
      const expected = casePatients[0];
      if (!filePatients.includes(expected)) {
        const where = filePatients.length === 0
          ? 'has no patient number'
          : `references Patient ${filePatients.join(', Patient ')}`;
        return `File name "${pdfFile.name}" does not match the selected case "${scenario.name}". Expected Patient ${expected}, but the filename ${where}.`;
      }
    }

    // Standalone fuzzy check for the remaining case-name words — this is
    // both the only check for cases without day/patient identifiers (e.g.
    // "Tucker Ingrown Toenail") and a secondary sanity check on the rest of
    // the case name when day/patient are present. day / patient / pure
    // numbers / short noise / common English stop words are excluded.
    const STOP_WORDS = new Set([
      'the', 'and', 'of', 'a', 'an', 'to', 'for', 'with', 'in', 'on',
      'day', 'patient',
    ]);
    const words = coreName.toLowerCase().split(/\s+/).filter(Boolean);
    const standalone = words
      .filter((word) => !/^\d{1,3}$/.test(word))
      .filter((word) => word.replace(/[^a-z0-9]/g, '').length >= 3)
      .filter((word) => !STOP_WORDS.has(word.replace(/[^a-z0-9]/g, '')));
    if (standalone.length === 0) return null;

    const foundCount = standalone
      .filter((word) => fileNorm.includes(normalize(word))).length;
    const needed = Math.max(1, Math.ceil(standalone.length / 2));
    if (foundCount < needed) {
      return `File name "${pdfFile.name}" does not appear to match the selected case "${scenario.name}". Please verify you are uploading the correct SOAP note.`;
    }
    return null;
  }

  const filenameMismatch = getFilenameMismatch();

  async function handleSubmit(e) {
    e.preventDefault();

    // Hard guard against double-submission. The Submit button has
    // disabled={loading} but a fast Enter-key press, multi-tab submit, or
    // race between click and re-render can still re-enter handleSubmit
    // before the first POST completes. Server-side dedup catches the
    // rest (submission_create.py rejects any duplicate VA+case row).
    if (loading) return;

    setError('');

    if (!selectedScenario) {
      setError('Please select a case.');
      return;
    }
    if (!pdfFile) {
      setError('Please upload your SOAP note PDF.');
      return;
    }
    if (filenameMismatch) {
      setError(filenameMismatch);
      return;
    }

    setLoading(true);
    try {
      const scenario = scenarios.find((s) => s.id === selectedScenario);
      const result = await submitSoapNote(
        selectedScenario,
        scenario?.name || '',
        pdfFile
      );
      if (result.error) {
        setError(result.error);
      } else {
        onSuccess(result);
      }
    } catch {
      setError('Failed to submit. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  const dropZoneStyle = {
    ...styles.dropZone,
    ...(dragActive ? styles.dropZoneActive : {}),
    ...(pdfFile ? styles.dropZoneHasFile : {}),
  };

  return (
    <div style={styles.card}>
      <h1 style={styles.heading}>Submit SOAP Note</h1>

      <div style={styles.infoBar}>
        <div style={styles.infoItem}>
          <div style={styles.infoLabel}>Name</div>
          <div style={styles.infoValue}>{session?.va_name || 'N/A'}</div>
        </div>
        <div style={styles.infoItem}>
          <div style={styles.infoLabel}>Batch</div>
          <div style={styles.infoValue}>{session?.batch || 'N/A'}</div>
        </div>
        <div style={styles.infoItem}>
          <div style={styles.infoLabel}>Submissions</div>
          <div style={{ ...styles.infoValue, color: subsUsed >= subsMax ? '#c53030' : '#1a202c' }}>
            {subsUsed} / {subsMax}
          </div>
        </div>
      </div>

      {subsUsed >= subsMax && (
        <div style={{ background: '#fed7d7', color: '#c53030', padding: '16px', borderRadius: '8px', marginBottom: '20px', fontSize: '14px', fontWeight: '600', textAlign: 'center' }}>
          You have reached the maximum of {subsMax} submissions for this batch.
        </div>
      )}

      {error && <div style={styles.error}>{error}</div>}

      <form onSubmit={handleSubmit}>
        <label style={styles.label}>Case</label>
        <select
          style={styles.select}
          value={selectedScenario}
          onChange={(e) => setSelectedScenario(e.target.value)}
          required
        >
          <option value="">Select a case...</option>
          {scenarios
            .filter((s) => !submissionHistory.some((h) => h.scenario_name === s.name))
            .map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
        </select>

        <label style={styles.label}>SOAP Note (PDF)</label>
        <div
          style={dropZoneStyle}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onClick={() => document.getElementById('pdf-input').click()}
        >
          {pdfFile ? (
            <>
              <div style={styles.fileName}>{pdfFile.name}</div>
              <div style={styles.fileSize}>{formatFileSize(pdfFile.size)}</div>
              <button
                type="button"
                style={styles.removeFile}
                onClick={(e) => {
                  e.stopPropagation();
                  setPdfFile(null);
                }}
              >
                Remove file
              </button>
            </>
          ) : (
            <>
              <div style={styles.dropIcon}>&#128196;</div>
              <div style={styles.dropText}>
                Drag & drop your SOAP note PDF here
              </div>
              <div style={styles.dropSubtext}>
                or click to browse (max 10 MB)
              </div>
            </>
          )}
        </div>
        <input
          id="pdf-input"
          type="file"
          accept=".pdf,application/pdf"
          style={{ display: 'none' }}
          onChange={(e) => {
            if (e.target.files[0]) handleFile(e.target.files[0]);
            e.target.value = '';
          }}
        />

        {filenameMismatch && (
          <div style={styles.warning}>
            <strong>Filename mismatch:</strong> {filenameMismatch}
          </div>
        )}

        <button
          style={{
            ...styles.button,
            ...(loading || !selectedScenario || !pdfFile || subsUsed >= subsMax || filenameMismatch
              ? styles.buttonDisabled
              : {}),
          }}
          type="submit"
          disabled={loading || !selectedScenario || !pdfFile || subsUsed >= subsMax || filenameMismatch}
        >
          {loading ? 'Submitting...' : 'Submit SOAP Note'}
        </button>
      </form>

      {/* Submission History */}
      {submissionHistory.length > 0 && (
        <div style={{ marginTop: '32px', borderTop: '2px solid #e2e8f0', paddingTop: '24px' }}>
          <h2 style={{ fontSize: '18px', fontWeight: '700', color: '#1a202c', marginBottom: '16px' }}>
            Previous Submissions ({submissionHistory.length})
          </h2>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
            <thead>
              <tr>
                <th style={{ padding: '8px 12px', textAlign: 'left', background: '#f7fafc', fontSize: '12px', fontWeight: '600', color: '#718096', textTransform: 'uppercase', letterSpacing: '0.5px' }}>#</th>
                <th style={{ padding: '8px 12px', textAlign: 'left', background: '#f7fafc', fontSize: '12px', fontWeight: '600', color: '#718096', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Case</th>
                <th style={{ padding: '8px 12px', textAlign: 'left', background: '#f7fafc', fontSize: '12px', fontWeight: '600', color: '#718096', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Date/Time Submitted</th>
              </tr>
            </thead>
            <tbody>
              {submissionHistory.map((sub, i) => {
                let dateDisplay = sub.submitted_at || '';
                try {
                  const d = new Date(sub.submitted_at);
                  if (!isNaN(d)) {
                    dateDisplay = d.toLocaleString('en-US', { timeZone: 'America/Phoenix', month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true }) + ' AZ';
                  }
                } catch {}
                return (
                  <tr key={sub.submission_id} style={{ borderTop: '1px solid #e2e8f0' }}>
                    <td style={{ padding: '10px 12px', color: '#718096' }}>{submissionHistory.length - i}</td>
                    <td style={{ padding: '10px 12px', color: '#4a5568', fontWeight: '500' }}>{sub.scenario_name}</td>
                    <td style={{ padding: '10px 12px', color: '#718096' }}>{dateDisplay}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
